// Cross-browser parsing for the date strings the API actually returns.
//
// The tournaments API serializes start_date/end_date with DOT separators and
// a space before the time - "2026.09.24 18:00" - which V8 (Chrome desktop,
// Android, Node) happily parses via its lenient fallback parser, but
// WebKit/iOS Safari rejects outright as an Invalid Date. Handing these
// straight to `new Date(string)` therefore worked everywhere except iPhone,
// where ClubCalendarTab's monthGroups builder dropped every tournament whose
// date it couldn't parse and rendered an empty club calendar.
//
// So: anything matching the plain "YYYY?MM?DD [HH:MM[:SS]]" shape (any of
// . - / as separator, T or space before the time) is parsed field-by-field
// into a LOCAL-time Date rather than trusted to the engine. Only strings
// carrying an explicit UTC marker or offset fall through to the engine,
// where that offset actually needs honouring.
//
// Always returns a fresh Date, never the caller's own instance - getMonday
// (and anything else that treats its argument as disposable) calls
// setHours/setDate directly on what it gets back, and handing over a shared
// object lets those mutations corrupt it out from under the caller.
export const parseDate = (val) => {
    if (!val) return null;
    if (val instanceof Date) return isNaN(val.getTime()) ? null : new Date(val.getTime());
    if (typeof val === "string") {
        const clean = val.trim();
        // An explicit trailing Z or ±HH:MM means the string is a real instant,
        // not a local wall-clock time - leave those to the engine below so the
        // offset is applied instead of silently ignored.
        const hasExplicitZone = /(?:[zZ]|[+-]\d{2}:?\d{2})$/.test(clean);
        if (!hasExplicitZone) {
            const m = clean.match(
                /^(\d{4})[-./](\d{1,2})[-./](\d{1,2})(?:[T\s](\d{1,2}):(\d{2})(?::(\d{2}))?)?$/
            );
            if (m) {
                return new Date(
                    Number(m[1]),
                    Number(m[2]) - 1,
                    Number(m[3]),
                    Number(m[4] || 0),
                    Number(m[5] || 0),
                    Number(m[6] || 0)
                );
            }
        }
        // Not one of the plain shapes above (or zoned): normalise what WebKit
        // is known to choke on - a space instead of T, and Django's default
        // 6-digit microseconds, which WebKit rejects for being longer than
        // the 3 fractional digits it accepts.
        const isoLike = clean
            .replace(/^(\d{4}-\d{1,2}-\d{1,2})\s+/, "$1T")
            .replace(/(\.\d{3})\d+/, "$1");
        const d = new Date(isoLike);
        return isNaN(d.getTime()) ? null : d;
    }
    const d = new Date(val);
    return isNaN(d.getTime()) ? null : d;
};

export default parseDate;
